import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import SmsService from '../../../services/smsService';

const SMSSendModal = ({ 
  isOpen, 
  onClose, 
  booking, 
  attendee, 
  onSuccess, 
  onError 
}) => {
  const [phoneNumber, setPhoneNumber] = useState(attendee?.phone || '');
  const [customMessage, setCustomMessage] = useState('');
  const [messageType, setMessageType] = useState('ticket'); // 'ticket' or 'custom'
  const [isLoading, setIsLoading] = useState(false);

  const handleSendSMS = async () => {
    if (!phoneNumber?.trim()) {
      onError?.('Please enter a phone number');
      return;
    }

    if (!SmsService?.validatePhoneNumber(phoneNumber)) {
      onError?.('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);

    try {
      let result;
      const formattedPhone = SmsService?.formatPhoneNumber(phoneNumber);

      if (messageType === 'ticket' && booking) {
        result = await SmsService?.sendTicketDetails(booking, formattedPhone);
      } else if (messageType === 'custom' && customMessage?.trim()) {
        result = await SmsService?.sendSMS(formattedPhone, customMessage);
      } else {
        onError?.('Please enter a message or select ticket details');
        setIsLoading(false);
        return;
      }

      if (result?.success) {
        onSuccess?.('SMS sent successfully!');
        onClose();
      } else {
        onError?.(result?.error || 'Failed to send SMS');
      }
    } catch (error) {
      onError?.(error?.message || 'Failed to send SMS');
    }

    setIsLoading(false);
  };

  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    if (type === 'ticket') {
      setCustomMessage('');
    }
  };

  if (!isOpen) return null;

  const ticketPreview = booking ? SmsService?.generateTicketSMS(booking) : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="MessageSquare" size={24} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Send SMS</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Recipient Info */}
          {attendee && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm font-medium text-foreground">
                {attendee?.name || attendee?.full_name}
              </p>
              <p className="text-xs text-muted-foreground">
                {attendee?.email}
              </p>
            </div>
          )}

          {/* Phone Number */}
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e?.target?.value)}
            required
            description="Include country code (e.g., +1 for US)"
          />

          {/* Message Type Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Message Type
            </label>
            
            {/* Ticket Details Option */}
            {booking && (
              <div className="flex items-start space-x-3">
                <input
                  type="radio"
                  id="ticket"
                  name="messageType"
                  value="ticket"
                  checked={messageType === 'ticket'}
                  onChange={() => handleMessageTypeChange('ticket')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <label htmlFor="ticket" className="text-sm font-medium text-foreground cursor-pointer">
                    Send Ticket Details
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Send complete event and booking information
                  </p>
                </div>
              </div>
            )}

            {/* Custom Message Option */}
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                id="custom"
                name="messageType"
                value="custom"
                checked={messageType === 'custom'}
                onChange={() => handleMessageTypeChange('custom')}
                className="mt-1"
              />
              <div className="flex-1">
                <label htmlFor="custom" className="text-sm font-medium text-foreground cursor-pointer">
                  Custom Message
                </label>
                <p className="text-xs text-muted-foreground">
                  Write your own message
                </p>
              </div>
            </div>
          </div>

          {/* Custom Message Input */}
          {messageType === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message
              </label>
              <textarea
                className="w-full h-24 px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                placeholder="Enter your message here..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e?.target?.value)}
                maxLength={1600}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {customMessage?.length || 0}/1600 characters
              </p>
            </div>
          )}

          {/* Message Preview */}
          {messageType === 'ticket' && ticketPreview && (
            <div className="bg-muted/50 rounded-lg p-3">
              <h3 className="text-sm font-medium text-foreground mb-2 flex items-center">
                <Icon name="Eye" size={16} className="mr-2" />
                Message Preview
              </h3>
              <div className="text-xs text-foreground whitespace-pre-wrap bg-background border border-border rounded p-2">
                {ticketPreview}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-xs text-muted-foreground">
            Standard SMS rates may apply
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSendSMS}
              disabled={isLoading || !phoneNumber?.trim()}
              iconName="Send"
              iconPosition="left"
            >
              {isLoading ? 'Sending...' : 'Send SMS'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSSendModal;